import React, { useContext, useEffect, useState, useCallback } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import { Col, Form, Spinner, Alert, Table, Badge } from 'react-bootstrap';
import { AppContext } from '../../App';
import { getAvailableKitComponents } from '../../clients/product_client';
import { showToast } from '../../config_and_helpers/helpers';
import DropdownMultiselect from '../Generic/DropdownMultiselect';
import RequiredAsteriskComponent from '../Generic/RequiredAsteriskComponent';
import NumberInput from '../Generic/NumberInput';
import { parseNumericInput } from '../../utils/numberInput';
import '../Glass/ProcureModalStyle.css';
import '../Glass/GlassStyle.css';

/**
 * Modal component for managing kit components within ProductModal.
 * Enhanced version that supports adding multiple components at once.
 */
const ProductKitComponentModal = ({
  show,
  onHide,
  onComponentUpdate,
  isEditMode = false,
  componentToEdit = null,
  existingComponents = [], // Array of existing components to filter out
}) => {
  const { token } = useContext(AppContext);

  // State for available components
  const [availableComponents, setAvailableComponents] = useState([]);
  const [loadingComponents, setLoadingComponents] = useState(false);

  // Form state for multiple components
  const [selectedComponents, setSelectedComponents] = useState([]);
  const [componentQuantities, setComponentQuantities] = useState({});
  const [componentRequired, setComponentRequired] = useState({});

  // Loading states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const loadAvailableComponents = useCallback(async () => {
    setLoadingComponents(true);
    setError(null);

    try {
      const components = await getAvailableKitComponents(token);

      // Validate that we have valid components with IDs
      if (!Array.isArray(components)) {
        throw new Error('Invalid response format from server');
      }

      const componentOptions = components
        .filter(comp => comp.id && comp.name && comp.cat_num) // Only include valid components
        .map(comp => ({
          value: comp.id,
          label: `${comp.name} (${comp.cat_num})`,
          name: comp.name,
          cat_num: comp.cat_num,
        }));

      if (componentOptions.length === 0) {
        setError('No valid components available for selection');
      } else {
        setAvailableComponents(componentOptions);
      }
    } catch (err) {
      // Error handled silently
      setError(
        'Failed to load available components: ' +
          (err.message || 'Unknown error')
      );
    } finally {
      setLoadingComponents(false);
    }
  }, [token]);

  // Filter out already selected components
  const getFilteredComponents = useCallback(() => {
    if (isEditMode && componentToEdit) {
      // In edit mode, show all components except the one being edited
      return availableComponents.filter(
        comp => comp.value !== componentToEdit.component.id
      );
    } else {
      // In add mode, filter out existing components
      const existingComponentIds = existingComponents.map(
        comp => comp.component.id
      );
      return availableComponents.filter(
        comp => !existingComponentIds.includes(comp.value)
      );
    }
  }, [availableComponents, isEditMode, componentToEdit, existingComponents]);

  // Load available components when modal opens
  useEffect(() => {
    if (show) {
      loadAvailableComponents();

      // If editing, populate form with existing data
      if (isEditMode && componentToEdit) {
        setSelectedComponents([
          {
            value: componentToEdit.component.id,
            label: `${componentToEdit.component.name} (${componentToEdit.component.cat_num})`,
            name: componentToEdit.component.name,
            cat_num: componentToEdit.component.cat_num,
          },
        ]);
        setComponentQuantities({
          [componentToEdit.component.id]: componentToEdit.quantity_per_kit,
        });
        setComponentRequired({
          [componentToEdit.component.id]: componentToEdit.is_required,
        });
      } else {
        // Reset form for new components
        setSelectedComponents([]);
        setComponentQuantities({});
        setComponentRequired({});
      }
    }
  }, [show, isEditMode, componentToEdit, loadAvailableComponents]);

  const handleSubmit = async () => {
    if (selectedComponents.length === 0) {
      setError('Please select at least one component');
      return;
    }

    // Validate that all selected components have valid IDs
    for (const component of selectedComponents) {
      if (!component.value || !component.name || !component.cat_num) {
        setError(
          `Invalid component data for ${
            component.name || 'Unknown'
          }. Please refresh and try again.`
        );
        return;
      }
    }

    // Validate quantities
    for (const component of selectedComponents) {
      const quantity = componentQuantities[component.value] || 1;
      if (quantity <= 0) {
        setError(`Quantity for ${component.name} must be greater than 0`);
        return;
      }
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (isEditMode && componentToEdit) {
        // Update existing component
        const componentData = {
          component_id: componentToEdit.component.id,
          component_name: componentToEdit.component.name,
          component_cat_num: componentToEdit.component.cat_num,
          quantity_per_kit:
            componentQuantities[componentToEdit.component.id] || 1,
          quantity_per_component:
            componentQuantities[componentToEdit.component.id] || 1,
          is_required: componentRequired[componentToEdit.component.id] || true,
        };

        onComponentUpdate(
          'update',
          componentToEdit.component.id,
          componentData
        );
        showToast(
          'Component updated successfully!',
          'success',
          'top-right',
          3000
        );
      } else {
        // Add multiple new components
        for (const component of selectedComponents) {
          const componentData = {
            component_id: component.value,
            component_name: component.name,
            component_cat_num: component.cat_num,
            quantity_per_kit: componentQuantities[component.value] || 1,
            quantity_per_component: componentQuantities[component.value] || 1,
            is_required: componentRequired[component.value] || true,
          };

          onComponentUpdate('add', null, componentData);
        }

        const componentCount = selectedComponents.length;
        showToast(
          `${componentCount} component${
            componentCount > 1 ? 's' : ''
          } added successfully!`,
          'success',
          'top-right',
          3000
        );
      }

      handleClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save component(s)');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedComponents([]);
    setComponentQuantities({});
    setComponentRequired({});
    setError(null);
    onHide();
  };

  const handleRemoveComponent = async () => {
    if (!componentToEdit) return;

    if (
      window.confirm(
        'Are you sure you want to remove this component from the kit?'
      )
    ) {
      setIsSubmitting(true);
      setError(null);

      try {
        onComponentUpdate('remove', componentToEdit.component.id);
        showToast(
          'Component removed successfully!',
          'success',
          'top-right',
          3000
        );

        handleClose();
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to remove component');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleQuantityChange = (componentId, quantity) => {
    setComponentQuantities(prev => ({
      ...prev,
      [componentId]: quantity,
    }));
  };

  const handleRequiredChange = (componentId, isRequired) => {
    setComponentRequired(prev => ({
      ...prev,
      [componentId]: isRequired,
    }));
  };

  const filteredComponents = getFilteredComponents();

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      size="lg"
      dialogClassName="procure-modal-glass procure-modal-glass-product"
    >
      <Modal.Header closeButton className="procure-modal__header">
        <Modal.Title>
          {isEditMode ? 'Edit Kit Component' : 'Add Kit Components'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="procure-modal__body">
        <Container>
          {error && (
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          )}

          <Row className="mb-3">
            <Col>
              <Form.Label>
                {isEditMode ? 'Component' : 'Components'}{' '}
                <RequiredAsteriskComponent />
              </Form.Label>
              {loadingComponents ? (
                <div className="d-flex justify-content-center">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2">Loading components...</span>
                </div>
              ) : (
                <DropdownMultiselect
                  optionsList={filteredComponents}
                  selectedValues={selectedComponents}
                  setSelectedValues={setSelectedComponents}
                  placeholder="components"
                />
              )}
              {!isEditMode && (
                <Form.Text className="text-muted">
                  Select one or more components to add to this kit
                </Form.Text>
              )}
            </Col>
          </Row>

          {/* Show selected components with quantity and required settings */}
          {selectedComponents.length > 0 && (
            <Row className="mb-3">
              <Col>
                <Form.Label>Component Settings</Form.Label>
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>Component</th>
                      <th>Quantity per Kit</th>
                      <th>Required</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedComponents.map(component => (
                      <tr key={component.value}>
                        <td>
                          <strong>{component.name}</strong>
                          <br />
                          <small className="text-muted">
                            {component.cat_num}
                          </small>
                        </td>
                        <td>
                          <NumberInput
                            name={`kitQty-${component.value}`}
                            integerOnly
                            value={String(componentQuantities[component.value] || 1)}
                            onChange={e => {
                              const parsed = parseNumericInput(e.target.value, {
                                integerOnly: true,
                              });
                              handleQuantityChange(
                                component.value,
                                parsed != null && parsed > 0 ? parsed : 1
                              );
                            }}
                            style={{ width: '80px' }}
                          />
                        </td>
                        <td>
                          <Form.Check
                            type="checkbox"
                            checked={
                              componentRequired[component.value] !== false
                            }
                            onChange={e =>
                              handleRequiredChange(
                                component.value,
                                e.target.checked
                              )
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Col>
            </Row>
          )}

          {/* Show existing components info */}
          {!isEditMode && existingComponents.length > 0 && (
            <Row className="mb-3">
              <Col>
                <Form.Label>Existing Components</Form.Label>
                <div className="border rounded p-2 bg-light">
                  {existingComponents.map((comp, index) => (
                    <Badge
                      key={comp.component.id}
                      bg="secondary"
                      className="me-2 mb-1"
                    >
                      {comp.component.name} (Qty: {comp.quantity_per_kit})
                      {comp.is_required && <span className="ms-1">*</span>}
                    </Badge>
                  ))}
                  <div className="text-muted small mt-1">
                    * Required components
                  </div>
                </div>
              </Col>
            </Row>
          )}
        </Container>
      </Modal.Body>

      <Modal.Footer className="procure-modal__footer">
        {isEditMode && (
          <Button
            variant="danger"
            onClick={handleRemoveComponent}
            disabled={isSubmitting}
            className="me-auto"
          >
            Remove Component
          </Button>
        )}

        <Button
          variant="secondary"
          className="procure-modal__close-btn"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Cancel
        </Button>

        <Button
          variant="primary"
          className="glass-primary-btn"
          onClick={handleSubmit}
          disabled={isSubmitting || selectedComponents.length === 0}
        >
          {isSubmitting ? (
            <>
              <Spinner
                size="sm"
                as="span"
                animation="border"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              {isEditMode ? 'Updating...' : 'Adding...'}
            </>
          ) : isEditMode ? (
            'Update Component'
          ) : (
            'Add Components'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ProductKitComponentModal;
